import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { API_BASE, apiPost, isInvalidSessionError } from "../api";
import { useAuth } from "../auth/AuthProvider";
import BaseButton from "../components/BaseButton/BaseButton";
import ExternalButton from "../components/BaseButton/ExternalButton";
import { useNavigationHeader } from "../components/MainLayout";
import PageHeader from "../components/PageHeader";
import { useAppData } from "../data/AppDataProvider";
import { debugHistory } from "../navigation/debug";

const PAYMENT_ORDER_STORAGE_KEY = "payment_order";
const PAYMENT_TTL_MS = 30 * 60 * 1000;

function parseExpireDate(value) {
    if (!value || typeof value !== "string") {
        return null;
    }

    const match = value.match(/^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/);
    if (!match) {
        return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]) - 1;
    const rawYear = Number(match[3]);
    const year = match[3].length === 2 ? 2000 + rawYear : rawYear;
    const parsedDate = new Date(year, month, day);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function addMonthsSafe(date, monthsToAdd) {
    const result = new Date(date);
    const day = result.getDate();

    result.setDate(1);
    result.setMonth(result.getMonth() + monthsToAdd);

    const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDayOfTargetMonth));

    return result;
}

function formatDate(date) {
    return date.toLocaleDateString("ru-RU");
}

function getDevicesLabel(devices) {
    if (devices === 1) {
        return "1 устройство";
    }

    if (devices < 5) {
        return `${devices} устройства`;
    }

    return `${devices} устройств`;
}

function buildPaymentState(selectedTariff, expire, orderResponse, paymentType) {
    const deadlineAt = Date.now() + PAYMENT_TTL_MS;
    return {
        ...selectedTariff,
        ...orderResponse,
        paymentType,
        fromPaytypeFlow: true,
        deadlineAt,
        currentExpire: expire.currentExpire,
        nextExpire: expire.nextExpire,
        orderType: selectedTariff.orderType || orderResponse.type || "tariff_pay",
    };
}

function buildYandexPaymentRedirectUrl(auth, orderType, devices, tariffKey) {
    const params = new URLSearchParams({
        token: auth?.token || "",
        type: orderType,
        devices: String(devices),
    });

    if (tariffKey) {
        params.set("tariff", tariffKey);
    }

    return `${API_BASE}/cabinet/yandex_pay_redirect?${params.toString()}`;
}

export default function Paytype() {
    const auth = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { mainPageData } = useAppData();
    const { shouldShowNavigation, setBottomActions } = useNavigationHeader();
    const [isSubmitting, setIsSubmitting] = useState("");
    const [error, setError] = useState("");
    const getAppearStyle = () => ({ "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s` });

    const selectedTariff = location.state || {};
    const devices = Number(selectedTariff.devices) || 1;
    const monthsCount = Number(selectedTariff.monthsCount) || 0;
    const monthsLabel = selectedTariff.monthsLabel || selectedTariff.orderLabel || "Не выбрано";
    const tariffKey = selectedTariff.monthsKey || "";
    const orderType = selectedTariff.orderType || "tariff_pay";
    const price = Number(selectedTariff.price) || 0;
    const yandexPaymentRedirectUrl = buildYandexPaymentRedirectUrl(auth, orderType, devices, tariffKey);

    const expireDate = parseExpireDate(mainPageData.expire);
    const now = new Date();
    const baseDate = expireDate && expireDate > now ? expireDate : now;
    const nextExpireDate = monthsCount > 0 ? addMonthsSafe(baseDate, monthsCount) : baseDate;
    const expireText = monthsCount > 0 ? formatDate(nextExpireDate) : (mainPageData.expire || "Не выбрано");

    const expireState = {
        currentExpire: mainPageData.expire || null,
        nextExpire: expireText,
    };

    const supportAction = useMemo(() => (
        <ExternalButton
            icon="icon-support"
            href="https://t.me/sodavpn_support"
            className="app-bottom-nav-btn"
            target="_blank"
            rel="noreferrer"
        >
            <span>Написать в поддержку</span>
        </ExternalButton>
    ), []);

    useEffect(() => {
        debugHistory("Paytype", "effect:mount-or-update", {
            locationState: location.state || null,
            selectedTariff: location.state || null,
        });
    }, [location.pathname, location.state]);

    useEffect(() => {
        if (!shouldShowNavigation) {
            setBottomActions(null);
            return undefined;
        }

        setBottomActions(supportAction);

        return () => {
            setBottomActions(null);
        };
    }, [setBottomActions, shouldShowNavigation, supportAction]);

    function handleYandexPaymentOpen(event) {
        if (!devices || (orderType !== "change_devices" && !tariffKey)) {
            event?.preventDefault();
            setError("Сначала выберите тариф");
            return;
        }

        if (!auth?.token) {
            event?.preventDefault();
            setError("Не удалось открыть оплату. Обновите страницу и попробуйте снова");
            return;
        }

        setError("");
        debugHistory("Paytype", "yandex-payment-select:open-link", {
            tariffKey,
            devices,
            monthsCount,
            selectedTariff,
        });
    }

    async function handlePaymentSelect(paymentType) {
        if (!devices || (orderType !== "change_devices" && !tariffKey)) {
            setError("Сначала выберите тариф");
            return;
        }

        setError("");
        setIsSubmitting(paymentType);
        debugHistory("Paytype", "payment-select:start", {
            paymentType,
            tariffKey,
            devices,
            monthsCount,
            selectedTariff,
        });

        try {
            const orderResponse = await apiPost(
                "/cabinet/payment_page_data",
                {
                    valute: paymentType,
                    type: orderType,
                    devices,
                    tariff: tariffKey || null,
                },
                auth,
            );

            const paymentState = buildPaymentState(
                {
                    ...selectedTariff,
                    devices,
                    price,
                },
                expireState,
                orderResponse,
                paymentType,
            );

            localStorage.setItem(PAYMENT_ORDER_STORAGE_KEY, JSON.stringify(paymentState));
            debugHistory("Paytype", "payment-select:navigate-payment", {
                paymentType,
                orderId: paymentState.order_id,
                paymentState,
            });
            navigate("/payment", { state: paymentState });
        } catch (error) {
            if (isInvalidSessionError(error)) {
                auth.logout();
                return;
            }

            setError("Не удалось создать платеж");
        } finally {
            setIsSubmitting("");
        }
    }

    return (
        <>
            <PageHeader title="Способы оплаты" />

            <div className="flex-col gap05 menu-appear" style={getAppearStyle()}>
                <div className="flex-between">
                    <span>{orderType === "change_devices" ? "Тип заказа" : "Тариф"}</span>
                    <span className="line-2"></span>
                    <span id="selected_date">{monthsLabel}</span>
                </div>
                <div className="flex-between">
                    <span>Количество устройств</span>
                    <span className="line-2"></span>
                    <span id="selected_devices">{getDevicesLabel(devices)}</span>
                </div>
                <div className="flex-between">
                    <span>{orderType === "change_devices" ? "Оплачено до" : "Доступ до"}</span>
                    <span className="line-2"></span>
                    <span id="selected_expire">{expireText}</span>
                </div>
            </div>

            {error ? <div className="attention-block pad1 menu-appear" style={getAppearStyle()}>{error}</div> : null}

            <ExternalButton
                icon="icon-sbp"
                href={yandexPaymentRedirectUrl}
                target="_blank"
                rel="noreferrer"
                referrerPolicy="no-referrer"
                onClick={handleYandexPaymentOpen}
                disabled={Boolean(isSubmitting)}
                className="menu-appear"
                style={getAppearStyle()}
            >
                <span>{`СБП / Картой${price > 0 ? ` · ${price} ₽` : ""}`}</span>
            </ExternalButton>

            <BaseButton
                icon="icon-bitcoin"
                onClick={() => handlePaymentSelect("btc")}
                disabled={Boolean(isSubmitting)}
                className="menu-appear"
                style={getAppearStyle()}
            >
                <span>{isSubmitting === "btc" ? "Создание..." : "BTC (Биткоин)"}</span>
            </BaseButton>

            <BaseButton
                icon="icon-trc20"
                onClick={() => handlePaymentSelect("usdt")}
                disabled={Boolean(isSubmitting)}
                className="menu-appear"
                style={getAppearStyle()}
            >
                <span>{isSubmitting === "usdt" ? "Создание..." : "USDT TRC20 (Криптовалюта)"}</span>
            </BaseButton>

            {!shouldShowNavigation ? supportAction : null}
        </>
    );
}
